import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
import AttachingDataAnalytics from './tabs/attach-data/attaching-data/analytics.md';
import AttachingDataTarget from './tabs/attach-data/attaching-data/target.md';
export const _frontmatter = {};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const InlineAlert = makeShortcode("InlineAlert");
const TabsBlock = makeShortcode("TabsBlock");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "attach-data-to-sdk-events"
    }}>{`Attach data to SDK events`}</h1>
    <p>{`The attach data_rule action is supported in `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/index.md"
      }}>{`Mobile Core`}</a>{` starting from version 2.1.8 (Launch), 2.3.5 (iOS), and 1.4.5 (Android). This action is powerful, complex, and enables advanced use cases.`}</p>
    <p>{`To use this action, you need to learn how events flow in the Adobe Experience Platform Mobile SDK and how they interact with the `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/rules-engine/index.md"
      }}>{`rules engine`}</a>{`.`}</p>
    <h2 {...{
      "id": "context"
    }}>{`Context`}</h2>
    <h3 {...{
      "id": "what-are-sdk-events"
    }}>{`What are SDK events?`}</h3>
    <p>{`In the Experience Platform Mobile SDK, events hold all the data that is required by other extensions to complete the necessary actions. Events have the following properties:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Type`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Describes the event. Examples include Adobe Analytics, Adobe Target, and Adobe Lifecycle.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Source`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Indicates the cause or the directionality of the event. For example, a request or a response.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Event data`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The data required to define the event. For example, context data on an Analytics event.`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`Extensions that register with `}<a parentName="p" {...{
        "href": "../../home/base/mobile-core/index.md"
      }}>{`Mobile Core`}</a>{` will also register event listeners. A listener is defined by a combination of event type and source. When the SDK event hub processes an event, it notifies all listeners that match the provided combination.`}</p>
    <h3 {...{
      "id": "how-are-events-created-in-the-sdk"
    }}>{`How are events created in the SDK?`}</h3>
    <p>{`Events are created by an extension and are dispatched to the SDK Event Hub. Each published rule that is created in the Data Collection UI is evaluated against the current event. Finally, the event is passed to each of the listeners for events with the submitted type / source combination.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Events are created and dispatched when an SDK public API is invoked. Attach data action use cases are meant to act on these types of events.`}</p>
    <h3 {...{
      "id": "what-is-the-rules-engine"
    }}>{`What is the Rules Engine?`}</h3>
    <p>{`The Rules Engine lives in the SDK Event Hub. Before listeners are notified, the Rules Engine evaluates each tag rule for mobile properties against the triggering event. A rule is defined by the following properties:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": "left"
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Event`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Trigger for the rule.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Condition`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Definition of the criteria to compare against the triggering event.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": "left"
          }}>{`Action`}</td>
          <td parentName="tr" {...{
            "align": "left"
          }}>{`The resulting action if the evaluation of the rule is positive.`}</td>
        </tr>
      </tbody>
    </table>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`A rule might be read out in the following way: If the SDK `}<strong parentName="p">{`Event`}</strong>{` occurs and `}<strong parentName="p">{`Condition(s)`}</strong>{` are met, then perform the `}<strong parentName="p">{`Action(s)`}</strong>{`.`}</p>
    <h2 {...{
      "id": "using-the-attach-data-action"
    }}>{`Using the attach data action`}</h2>
    <p><strong parentName="p">{`Attach Data`}</strong>{` is a type of rule action that lets you add event data to an SDK event. The modification of data happens in the Rules Engine before event listeners are notified of the event.`}</p>
    <InlineAlert variant="info" slots="text" mdxType="InlineAlert" />
    <p>{`Attach data rule actions will only add data to the event. These actions never modify or remove data. `}<br /><br />{` If there is a conflict between the data that is defined in your rule and the data in the event, the data in the event always has preference.`}</p>
    <h3 {...{
      "id": "defining-a-payload-for-the-attach-data-action"
    }}>{`Defining a payload for the attach data action`}</h3>
    <p>{`When defining a payload for the attach data action, the payload must match the format of the triggering event. For example, if you want to add context data to an Adobe Analytics event, you need to know where the context data is defined on that event and match the format in your rule.`}</p>
    <p>{`As a result, it is highly recommended to enable verbose logging in the SDK and carefully study the format of the event to which you will attach the data. If the format does not match, most likely the expected results will not be received.`}</p>
    <h2 {...{
      "id": "example---attaching-data-to-an-event"
    }}>{`Example - attaching data to an event`}</h2>
    <TabsBlock orientation="horizontal" slots="heading, content" repeat="2" mdxType="TabsBlock" />
    <p>{`Analytics`}</p>
    <AttachingDataAnalytics mdxType="AttachingDataAnalytics" />
    <p>{`Target`}</p>
    <AttachingDataTarget mdxType="AttachingDataTarget" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      